import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  Modal,
  Checkbox,
  Select,
  Switch,
  Upload,
  message,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  analyzeAllPolicyDocuments,
  api,
  createEditablePolicyCopy,
  deletePolicyDocument,
  downloadCombinedPoliciesExcel,
  downloadCombinedPoliciesPdf,
  downloadPolicyAnalysisPdf,
  fetchPolicyInsuranceTypes,
  fetchRelevantTenderTemplates,
  reanalyzePolicyDocument,
  replacePolicyInsuranceTypes,
  selectTenderTemplateForPolicy,
  updateEditablePolicyCopy,
  type PolicyExportType,
  type RelevantTenderTemplatesResponse
} from './api';
import {
  createLeadTodo,
  fetchLeadTodoEvents,
  fetchLeadTodos,
  fetchTodoAssignees,
  updateLeadTodo,
  type LeadTodoPayload
} from './leadTodosApi';
import TenderWorkspace from './TenderWorkspace';
import {
  formatAmount,
  formatCvrNumber,
  statusLabelMap,
  statusTagColors,
  formatDateTimeShort,
  formatDateTimeFull,
  leadTierDefinitionMap,
  leadTierTagColors,
  deriveLeadTier,
  todoStatusLabels,
  todoStatusColors,
  todoPriorityLabels,
  todoPriorityColors
} from './constants';
import type {
  CvrSummary,
  Label,
  LeadGroup,
  LeadGroupMember,
  LeadPolicyAnalysisEdit,
  LeadPolicyDocument,
  LeadTodo,
  LeadTodoEvent,
  PolicyEditablePayload,
  TodoAssignee
} from './types';
import { InsuranceType, LeadInsuranceType } from './types';
import {
  normalizeEditablePolicyPayload,
  toEditablePayloadFromDocument,
  type PolicyEditableArrayKey
} from './policyEditableUtils';
import {
  formatDateTime,
  leadEventTypeLabels,
  policyStatusColors,
  policyStatusLabels,
  toLocalInputValue
} from './leadDetailUtils';
import PolicyAnalysisActions from './PolicyAnalysisActions';
import PolicyAnalysisDetails from './PolicyAnalysisDetails';

const { Text, Paragraph } = Typography;

const AZURE_DI_CONFIG_ERROR_PATTERN = /Azure Document Intelligence er ikke konfigureret/i;

function extractWorkListLink(description: string) {
  const match = description.match(/(\/\?view=arbejdslister&workListId=[^\s]+)/i);
  return match ? match[1] : null;
}

function toPolicyUiErrorMessage(error: unknown, fallback: string): string {
  const rawMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (AZURE_DI_CONFIG_ERROR_PATTERN.test(rawMessage)) {
    return 'Azure Document Intelligence mangler serverkonfiguration (endpoint + nøgle). Kontakt en administrator.';
  }
  return rawMessage || fallback;
}

type LeadEvent = {
  id: string;
  event_type: string;
  actor_user_id: string;
  from_status?: string | null;
  to_status?: string | null;
  created_at: string;
  notes?: string | null;
};

type LeadMeeting = {
  id: string;
  scheduled_for: string | null;
  location: string | null;
  summary: string | null;
  created_at: string;
};

type TodoFormValues = {
  title: string;
  description?: string;
  status: LeadTodo['status'];
  priority: LeadTodo['priority'];
  due_at?: Dayjs | null;
  assigned_to?: string | null;
  sync_to_calendar?: boolean;
};

type LeadDetailResponse = {
  lead: any;
  events: LeadEvent[];
  meetings: LeadMeeting[];
  labels?: Label[];
  cvrSummary?: CvrSummary | null;
};

type PolicyEditDraft = {
  summary: string;
  payload: PolicyEditablePayload;
};

type LeadSearchResult = {
  id: string;
  company_name: string;
  cvrnummer: string | null;
  status: string;
};

interface LeadDetailPageProps {
  canManage: boolean;
  onBack: (state?: { fromCompanySearch?: string | null }) => void;
  onLeadUpdated?: (leadId?: string | null) => void | Promise<void>;
  leadIdOverride?: string | null;
  locationStateOverride?: { fromCompanySearch?: string | null };
  hideInsuranceTypes?: boolean;
}

export default function LeadDetailPage({
  canManage,
  onBack,
  onLeadUpdated,
  leadIdOverride,
  locationStateOverride,
  hideInsuranceTypes = false
}: LeadDetailPageProps) {
  const params = useParams<{ leadId: string }>();
  const location = useLocation();
  const hideInsurance = !canManage;
  const effectiveLeadId = leadIdOverride ?? params.leadId ?? null;
  const leadId = effectiveLeadId;
  const backLocationState = useMemo(
    () => locationStateOverride ?? ((location.state as { fromCompanySearch?: string | null }) ?? undefined),
    [location.state, locationStateOverride]
  );
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState<any | null>(null);
  const [leadGroup, setLeadGroup] = useState<LeadGroup | null>(null);
  const [leadGroupLoading, setLeadGroupLoading] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState<'create' | 'add'>('create');
  const [selectedLinkLeadId, setSelectedLinkLeadId] = useState<string | null>(null);
  const [linkModalSubmitting, setLinkModalSubmitting] = useState(false);
  const [leadSearchResults, setLeadSearchResults] = useState<LeadSearchResult[]>([]);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const leadSearchTimeoutRef = useRef<number | null>(null);
  const [removingGroupMemberId, setRemovingGroupMemberId] = useState<string | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [meetings, setMeetings] = useState<LeadMeeting[]>([]);
  const [cvrSummary, setCvrSummary] = useState<CvrSummary | null>(null);
  const [initialCvrSummary, setInitialCvrSummary] = useState<CvrSummary | null>(null);
  const [cvrLookupLoading, setCvrLookupLoading] = useState(false);
  const [cvrLookupError, setCvrLookupError] = useState<string | null>(null);
  const cvrLookupSeq = useRef(0);
  const [notesForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [todoForm] = Form.useForm();
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingMeeting, setSavingMeeting] = useState(false);
  const [savingTodo, setSavingTodo] = useState(false);
  const [leadLabels, setLeadLabels] = useState<Label[]>([]);
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [removingLabelId, setRemovingLabelId] = useState<string | null>(null);
  const [callAttemptLoading, setCallAttemptLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [deletingMeetingId, setDeletingMeetingId] = useState<string | null>(null);
  const [editingMeeting, setEditingMeeting] = useState<LeadMeeting | null>(null);
  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<LeadTodo | null>(null);
  const [todos, setTodos] = useState<LeadTodo[]>([]);
  const [todosLoading, setTodosLoading] = useState(false);
  const [todoHistoryModalOpen, setTodoHistoryModalOpen] = useState(false);
  const [todoHistoryLoading, setTodoHistoryLoading] = useState(false);
  const [todoHistory, setTodoHistory] = useState<LeadTodoEvent[]>([]);
  const [historyTodo, setHistoryTodo] = useState<LeadTodo | null>(null);
  const [assignees, setAssignees] = useState<TodoAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState<InsuranceType[]>([]);
  const [insuranceTypesLoading, setInsuranceTypesLoading] = useState(false);
  const [policyInsuranceTypesById, setPolicyInsuranceTypesById] = useState<Record<string, LeadInsuranceType[]>>({});
  const [policyInsuranceLoadingById, setPolicyInsuranceLoadingById] = useState<Record<string, boolean>>({});
  const [policyTemplatesById, setPolicyTemplatesById] = useState<Record<string, RelevantTenderTemplatesResponse>>({});
  const [policyTemplateLoadingById, setPolicyTemplateLoadingById] = useState<Record<string, boolean>>({});
  const [policyTemplateSavingById, setPolicyTemplateSavingById] = useState<Record<string, boolean>>({});
  const [policyInsuranceModalOpen, setPolicyInsuranceModalOpen] = useState(false);
  const [policyInsuranceModalSaving, setPolicyInsuranceModalSaving] = useState(false);
  const [policyInsuranceModalPolicyId, setPolicyInsuranceModalPolicyId] = useState<string | null>(null);
  const [selectedPolicyInsuranceTypeIds, setSelectedPolicyInsuranceTypeIds] = useState<string[]>([]);
  const [removingPolicyInsuranceKey, setRemovingPolicyInsuranceKey] = useState<string | null>(null);
  const [policyDocuments, setPolicyDocuments] = useState<LeadPolicyDocument[]>([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyUploading, setPolicyUploading] = useState(false);
  const [policyFileList, setPolicyFileList] = useState<UploadFile[]>([]);
  const [policyEditDrafts, setPolicyEditDrafts] = useState<Record<string, PolicyEditDraft>>({});
  const [policySavingById, setPolicySavingById] = useState<Record<string, boolean>>({});
  const [policyCopyCreatingById, setPolicyCopyCreatingById] = useState<Record<string, boolean>>({});
  const [policyReanalyzingById, setPolicyReanalyzingById] = useState<Record<string, boolean>>({});
  const [policyAnalyzeAllLoading, setPolicyAnalyzeAllLoading] = useState(false);
  const [policyAnalyzeAllBackground, setPolicyAnalyzeAllBackground] = useState(false);
  const [policyAnalyzeAllSawPending, setPolicyAnalyzeAllSawPending] = useState(false);
  const [analysisMessageApi, analysisMessageContextHolder] = message.useMessage();
  const [combinedPolicyExportType, setCombinedPolicyExportType] = useState<PolicyExportType>('tender');
  const [combinedPolicyExportLoading, setCombinedPolicyExportLoading] = useState(false);
  const [combinedPolicyExcelExportLoading, setCombinedPolicyExcelExportLoading] = useState(false);
  const policyAnalyzeAllStartedAtRef = useRef<number | null>(null);
  const watchedCvrValue = Form.useWatch('cvrnummer', notesForm);
  const watchedTodoDue = Form.useWatch('due_at', todoForm);
  const savedLeadCvr = lead?.cvrnummer || '';
  const callAttemptCount = Number(lead?.call_attempt_count ?? 0);
  const isHidden = Boolean(lead?.hidden_at);
  const lastCallShort = formatDateTimeShort(lead?.last_call_at ?? null);
  const lastCallFull = formatDateTimeFull(lead?.last_call_at ?? null);

  useEffect(() => {
    if (!watchedTodoDue) {
      todoForm.setFieldsValue({ sync_to_calendar: false });
    }
  }, [watchedTodoDue, todoForm]);

  const commissionPercentRaw = lead?.commission_percent;
  const commissionPercent =
    commissionPercentRaw === undefined || commissionPercentRaw === null
      ? null
      : Number(commissionPercentRaw);
  const contractValueRaw = lead?.contract_value_annual;
  const contractValue =
    contractValueRaw === undefined || contractValueRaw === null ? null : Number(contractValueRaw);
  const commissionValue =
    commissionPercent !== null && contractValue !== null && !Number.isNaN(contractValue)
      ? (contractValue * commissionPercent) / 100
      : null;
  const commissionDisplay =
    commissionPercent === null || Number.isNaN(commissionPercent)
      ? '-'
      : `${commissionPercent}%${
          commissionValue !== null && !Number.isNaN(commissionValue)
            ? ` (${formatAmount(commissionValue, lead?.currency)})`
            : ''
        }`;
  const employeesRaw = lead?.cvr_ansatte_aar ?? lead?.ansatte_aar;
  const employeesCount = employeesRaw === undefined || employeesRaw === null ? null : Number(employeesRaw);
  const employeesDisplay =
    employeesCount === null || Number.isNaN(employeesCount) ? employeesRaw ?? null : employeesCount;
  const employeesDateRaw = lead?.cvr_ansatte_datotil ?? lead?.ansatte_datotil;
  const employeesYear = (() => {
    if (!employeesDateRaw) return null;
    const parsed = new Date(employeesDateRaw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getFullYear().toString();
    }
    const yearCandidate = String(employeesDateRaw).slice(0, 4);
    return yearCandidate.length === 4 ? yearCandidate : String(employeesDateRaw);
  })();
  const formattedCvr = formatCvrNumber(lead?.cvrnummer) || '';
  const leadTier = deriveLeadTier(lead?.status);
  const tierMeta = leadTierDefinitionMap.get(leadTier);
  const tierLabel =
    tierMeta?.label ??
    (leadTier === 'CUSTOMER'
      ? 'Kunde'
      : leadTier === 'IN_PROGRESS'
        ? 'Sag'
        : 'Lead');
  const tierDescription = tierMeta?.description;
  const tierTagColor = leadTierTagColors[leadTier] || 'default';

  const cvrAddress = useMemo(() => {
    if (!cvrSummary) {
      return '';
    }
    const segments: string[] = [];
    const streetParts: string[] = [];
    if (cvrSummary.vejnavn) {
      streetParts.push(cvrSummary.vejnavn.trim());
    }
    const from = cvrSummary.husnummer_fra;
    const to = cvrSummary.husnummer_til;
    let houseNumber = '';
    if (from && to && to !== from) {
      houseNumber = `${from}-${to}`;
    } else if (from) {
      houseNumber = from;
    } else if (to) {
      houseNumber = to;
    }
    if (houseNumber) {
      streetParts.push(houseNumber);
    }
    const detailParts: string[] = [];
    if (cvrSummary.etage) {
      detailParts.push(`${cvrSummary.etage}.`);
    }
    if (cvrSummary.doer) {
      detailParts.push(cvrSummary.doer);
    }
    if (detailParts.length) {
      streetParts.push(detailParts.join(' '));
    }
    if (streetParts.length) {
      segments.push(streetParts.join(' ').replace(/\s+/g, ' ').trim());
    }
    if (cvrSummary.supplerende_bynavn) {
      segments.push(cvrSummary.supplerende_bynavn);
    }
    const postalParts: string[] = [];
    if (cvrSummary.postnummer) {
      postalParts.push(cvrSummary.postnummer);
    }
    if (cvrSummary.postdistrikt) {
      postalParts.push(cvrSummary.postdistrikt);
    }
    if (postalParts.length) {
      segments.push(postalParts.join(' '));
    }
    if (cvrSummary.kommunenavn) {
      segments.push(cvrSummary.kommunenavn);
    }
    return segments.join(', ');
  }, [cvrSummary]);

  const sortedLeadLabels = useMemo(
    () => [...leadLabels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' })),
    [leadLabels]
  );

  const unassignedLabels = useMemo(
    () => allLabels.filter((label) => !leadLabels.some((assigned) => assigned.id === label.id)),
    [allLabels, leadLabels]
  );

  const completedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'COMPLETED').length,
    [policyDocuments]
  );

  const notCompletedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status !== 'COMPLETED').length,
    [policyDocuments]
  );

  const pendingPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'PENDING').length,
    [policyDocuments]
  );

  const failedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'FAILED').length,
    [policyDocuments]
  );

  const labelOptions = useMemo(
    () =>
      unassignedLabels.map((label) => ({
        value: label.id,
        label: label.hideInSearch ? `${label.name} (Skjul i søgeresultat)` : label.name
      })),
    [unassignedLabels]
  );

  const validateOptionalCvr = useCallback((_: unknown, value: string | undefined) => {
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }
    const digits = `${value}`.replace(/\D+/g, '');
    if (!digits) {
      return Promise.resolve();
    }
    if (digits.length === 8) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('CVR-nummer skal bestå af 8 cifre'));
  }, []);

  const loadInsuranceTypes = useCallback(async () => {
    if (hideInsurance || !canManage) {
      setAvailableInsuranceTypes([]);
      return;
    }
    setInsuranceTypesLoading(true);
    try {
      const res = await api('/api/insurance-types');
      const data = (await res.json().catch(() => [])) as InsuranceType[];
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper');
      }
      const normalized = Array.isArray(data) ? data : [];
      setAvailableInsuranceTypes(
        [...normalized].sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      console.error('Failed to load insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper');
    } finally {
      setInsuranceTypesLoading(false);
    }
  }, [canManage, hideInsurance]);

  const normalizePolicyInsuranceItems = useCallback((items: LeadInsuranceType[]) => {
    const normalized = Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          insuranceTypeId: String(item.insuranceTypeId ?? (item as any)?.insurancetypeid ?? ''),
          title: item.title ?? '',
          category: item.category ?? '',
          description: item.description ?? null
        }))
      : [];

    return normalized.sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }));
  }, []);

  const loadPolicyInsuranceForDocuments = useCallback(async (documents: LeadPolicyDocument[]) => {
    if (hideInsurance || !leadId || !canManage || documents.length === 0) {
      setPolicyInsuranceTypesById({});
      return;
    }

    const loadingEntries = documents.reduce<Record<string, boolean>>((acc, doc) => {
      acc[doc.id] = true;
      return acc;
    }, {});
    setPolicyInsuranceLoadingById(loadingEntries);

    try {
      const entries = await Promise.all(
        documents.map(async (doc) => {
          const payload = await fetchPolicyInsuranceTypes(leadId, doc.id);
          return [doc.id, normalizePolicyInsuranceItems(payload.items || [])] as const;
        })
      );

      setPolicyInsuranceTypesById(Object.fromEntries(entries));
    } catch (error: any) {
      console.error('Failed to load policy insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper for policer');
      setPolicyInsuranceTypesById({});
    } finally {
      setPolicyInsuranceLoadingById({});
    }
  }, [canManage, hideInsurance, leadId, normalizePolicyInsuranceItems]);

  const loadPolicyTemplatesForDocuments = useCallback(async (documents: LeadPolicyDocument[]) => {
    if (!leadId || documents.length === 0) {
      setPolicyTemplatesById({});
      setPolicyTemplateLoadingById({});
      return;
    }

    setPolicyTemplateLoadingById(documents.reduce<Record<string, boolean>>((current, document) => {
      current[document.id] = true;
      return current;
    }, {}));
    const entries = await Promise.all(documents.map(async (document) => {
      try {
        return [document.id, await fetchRelevantTenderTemplates(leadId, document.id)] as const;
      } catch (_error) {
        return [document.id, null] as const;
      }
    }));
    setPolicyTemplatesById(Object.fromEntries(entries.filter((entry): entry is readonly [string, RelevantTenderTemplatesResponse] => entry[1] !== null)));
    setPolicyTemplateLoadingById({});
  }, [leadId]);

  useEffect(() => {
    if (hideInsurance) {
      return;
    }
    void loadInsuranceTypes();
  }, [hideInsurance, loadInsuranceTypes]);

  const loadLeadPolicies = useCallback(async () => {
    if (!leadId) {
      setPolicyDocuments([]);
      setPolicyEditDrafts({});
      setPolicyInsuranceTypesById({});
      setPolicyInsuranceLoadingById({});
      return;
    }
    setPolicyLoading(true);
    try {
      const res = await api(`/api/leads/${leadId}/policies`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente policer');
      }
      const docs = Array.isArray((data as any)?.documents) ? (data as any).documents : [];
      setPolicyDocuments(docs as LeadPolicyDocument[]);
      setPolicyEditDrafts({});
      await Promise.all([
        loadPolicyInsuranceForDocuments(docs as LeadPolicyDocument[]),
        loadPolicyTemplatesForDocuments(docs as LeadPolicyDocument[])
      ]);
    } catch (error: any) {
      console.error('Failed to load lead policies', error);
      message.error(error?.message || 'Kunne ikke hente policer');
      setPolicyInsuranceTypesById({});
      setPolicyInsuranceLoadingById({});
    } finally {
      setPolicyLoading(false);
    }
  }, [leadId, loadPolicyInsuranceForDocuments, loadPolicyTemplatesForDocuments]);

  const loadTodos = useCallback(async () => {
    if (!leadId || !canManage) {
      setTodos([]);
      return;
    }
    try {
      setTodosLoading(true);
      const data = await fetchLeadTodos(leadId);
      setTodos(data);
    } catch (error: any) {
      console.error('Failed to load lead todos', error);
      message.error(error?.message || 'Kunne ikke hente opgaver');
    } finally {
      setTodosLoading(false);
    }
  }, [canManage, leadId]);

  const loadTodoAssignees = useCallback(async () => {
    if (!canManage) {
      setAssignees([]);
      return;
    }
    try {
      setAssigneesLoading(true);
      const data = await fetchTodoAssignees();
      setAssignees(data);
    } catch (error: any) {
      console.error('Failed to load todo assignees', error);
      message.error(error?.message || 'Kunne ikke hente brugere');
    } finally {
      setAssigneesLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    void loadLeadPolicies();
  }, [loadLeadPolicies]);

  useEffect(() => {
    if (!policyAnalyzeAllBackground || !leadId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadLeadPolicies();
    }, 5000);

    return () => {
      window.clearInterval(timer);
    };
  }, [leadId, loadLeadPolicies, policyAnalyzeAllBackground]);

  useEffect(() => {
    if (!policyAnalyzeAllBackground) {
      setPolicyAnalyzeAllSawPending(false);
      return;
    }

    if (pendingPolicyCount > 0) {
      if (!policyAnalyzeAllSawPending) {
        setPolicyAnalyzeAllSawPending(true);
      }
      return;
    }

    const startedAt = policyAnalyzeAllStartedAtRef.current;
    const elapsed = startedAt ? Date.now() - startedAt : 0;
    const waitLongEnoughWithoutPending = !policyAnalyzeAllSawPending && elapsed >= 15000;

    if (!policyAnalyzeAllSawPending && !waitLongEnoughWithoutPending) {
      return;
    }

    setPolicyAnalyzeAllBackground(false);
    policyAnalyzeAllStartedAtRef.current = null;

    if (failedPolicyCount > 0) {
      analysisMessageApi.warning(`AI-analyse afsluttet. ${failedPolicyCount} dokumenter kræver manuel gennemgang.`);
    } else {
      analysisMessageApi.success('AI-analyse afsluttet. Alle dokumenter er opdateret.');
    }
  }, [analysisMessageApi, failedPolicyCount, pendingPolicyCount, policyAnalyzeAllBackground, policyAnalyzeAllSawPending]);

  const handlePolicyUpload = useCallback(async () => {
    if (!leadId || policyFileList.length === 0) {
      return;
    }
    setPolicyUploading(true);
    try {
      const formData = new FormData();
      policyFileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });
      const res = await api(`/api/leads/${leadId}/policies`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke uploade policer');
      }
      message.success('Policer uploadet');
      setPolicyFileList([]);
      await loadLeadPolicies();
      
      // Start analysis automatically after successful upload
      try {
        const result = await analyzeAllPolicyDocuments(leadId, false, true);
        if (result.accepted) {
          policyAnalyzeAllStartedAtRef.current = Date.now();
          setPolicyAnalyzeAllSawPending(false);
          setPolicyAnalyzeAllBackground(true);
          analysisMessageApi.info('AI-analyse kører i baggrunden. Du får besked her, når den er færdig.');
        }
      } catch (analyzeError) {
        console.warn('Failed to start auto-analysis', analyzeError);
      }
    } catch (error: any) {
      console.error('Failed to upload lead policies', error);
      message.error(toPolicyUiErrorMessage(error, 'Kunne ikke uploade policer'));
    } finally {
      setPolicyUploading(false);
    }
  }, [leadId, loadLeadPolicies, policyFileList, analysisMessageApi]);

  const handleDeletePolicy = useCallback((doc: LeadPolicyDocument) => {
    if (!leadId) {
      return;
    }

    Modal.confirm({
      title: 'Slet police?',
      content: `Er du sikker på, at du vil slette ${doc.file_name || 'dette dokument'}?`,
      okText: 'Slet',
      okButtonProps: { danger: true },
      cancelText: 'Annuller',
      onOk: async () => {
        try {
          await deletePolicyDocument(leadId, doc.id);
          message.success('Policedokument slettet');
          await loadLeadPolicies();
        } catch (error: any) {
          console.error('Failed to delete policy document', error);
          message.error(error?.message || 'Kunne ikke slette policedokument');
        }
      }
    });
  }, [leadId, loadLeadPolicies]);

  const handlePolicyTemplateChange = useCallback(async (policyId: string, templateId: string) => {
    if (!leadId) return;
    setPolicyTemplateSavingById((current) => ({ ...current, [policyId]: true }));
    try {
      await selectTenderTemplateForPolicy(leadId, policyId, templateId);
      setPolicyTemplatesById((current) => ({
        ...current,
        [policyId]: current[policyId] ? { ...current[policyId], selectedTemplateId: templateId } : current[policyId]
      }));
      message.success('Udbudsskabelon valgt for policen');
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke vælge udbudsskabelon');
    } finally {
      setPolicyTemplateSavingById((current) => ({ ...current, [policyId]: false }));
    }
  }, [leadId]);

  const handlePolicyDownload = useCallback(async (doc: LeadPolicyDocument) => {
    try {
      const res = await api(doc.download_url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.error || 'Kunne ikke hente dokumentet');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'policy-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download policy', error);
      message.error(error?.message || 'Kunne ikke hente dokumentet');
    }
  }, []);

  const handlePolicyViewOriginal = useCallback(async (doc: LeadPolicyDocument) => {
    const targetUrl = typeof doc.view_url === 'string' && doc.view_url.trim() ? doc.view_url : doc.download_url;
    const previewWindow = window.open('', '_blank', 'noopener,noreferrer');

    try {
      const res = await api(targetUrl);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.error || 'Kunne ikke åbne original policedokument');
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (error: any) {
      if (previewWindow) {
        previewWindow.close();
      }
      console.error('Failed to view original policy', error);
      message.error(error?.message || 'Kunne ikke åbne original policedokument');
    }
  }, []);

  const handlePolicyPdfExport = useCallback(
    async (doc: LeadPolicyDocument, type: PolicyExportType) => {
      if (!leadId) {
        return;
      }

      try {
        const blob = await downloadPolicyAnalysisPdf(leadId, doc.id, type);
        const url = window.URL.createObjectURL(blob);
        const baseName = (doc.file_name || 'policy-document').replace(/\.[^.]+$/, '');
        const suffix = type === 'tender' ? 'udbudsmateriale' : 'aftaledokument';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}-${suffix}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        console.error('Failed to export policy PDF', error);
        message.error(error?.message || 'Kunne ikke generere PDF');
      }
    },
    [leadId]
  );

  const handleCombinedPolicyPdfExport = useCallback(async () => {
    if (!leadId || completedPolicyCount === 0) {
      return;
    }

    setCombinedPolicyExportLoading(true);
    try {
      const blob = await downloadCombinedPoliciesPdf(leadId, combinedPolicyExportType);
      const url = window.URL.createObjectURL(blob);
      const companyBaseName = (lead?.company_name || 'lead').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const suffix = combinedPolicyExportType === 'tender' ? 'udbudsmateriale' : 'aftaledokument';
      const link = document.createElement('a');
      link.href = url;
      link.download = `${companyBaseName}-samlet-${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export combined policy PDF', error);
      message.error(error?.message || 'Kunne ikke generere samlet PDF');
    } finally {
      setCombinedPolicyExportLoading(false);
    }
  }, [combinedPolicyExportType, completedPolicyCount, lead?.company_name, leadId]);

  const handleCombinedPolicyExcelExport = useCallback(async () => {
    if (!leadId || completedPolicyCount === 0) {
      return;
    }

    setCombinedPolicyExcelExportLoading(true);
    try {
      const blob = await downloadCombinedPoliciesExcel(leadId);
      const url = window.URL.createObjectURL(blob);
      const companyBaseName = (lead?.company_name || 'lead').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${companyBaseName}-samlet-udbudsmateriale.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export combined policy Excel', error);
      message.error(error?.message || 'Kunne ikke generere samlet Excel');
    } finally {
      setCombinedPolicyExcelExportLoading(false);
    }
  }, [completedPolicyCount, lead?.company_name, leadId]);

  const handleAnalyzeAllPolicies = useCallback(async () => {
    if (!leadId || policyDocuments.length === 0) {
      return;
    }

    let includeCompleted = false;
    let modalRef: ReturnType<typeof Modal.confirm> | null = null;

    const buildModalContent = (checked: boolean) => {
      const targetCount = checked ? policyDocuments.length : notCompletedPolicyCount;
      return (
        <Space direction="vertical" size={8}>
          <Text>
            Der analyseres {targetCount} dokumenter.
            {checked
              ? ' Allerede analyserede dokumenter analyseres også igen.'
              : ' Allerede analyserede dokumenter springes over.'}
          </Text>
          <Checkbox
            checked={checked}
            onChange={(event) => {
              includeCompleted = event.target.checked;
              modalRef?.update({
                title: includeCompleted ? 'AI-analysér alle uploadede policer?' : 'AI-analysér alle manglende policer?',
                content: buildModalContent(includeCompleted)
              });
            }}
          >
            Analysér også dokumenter der allerede er analyseret
          </Checkbox>
        </Space>
      );
    };

    modalRef = Modal.confirm({
      title: 'AI-analysér alle manglende policer?',
      content: buildModalContent(includeCompleted),
      okText: 'Start analyse',
      cancelText: 'Annuller',
      onOk: async () => {
        setPolicyAnalyzeAllLoading(true);
        try {
          const result = await analyzeAllPolicyDocuments(leadId, includeCompleted, true);

          if (result.accepted) {
            policyAnalyzeAllStartedAtRef.current = Date.now();
            setPolicyAnalyzeAllSawPending(false);
            setPolicyAnalyzeAllBackground(true);
            await loadLeadPolicies();

            if (result.analyzed_documents === 0) {
              setPolicyAnalyzeAllBackground(false);
              analysisMessageApi.info('Alle uploadede policer var allerede analyseret');
              return;
            }

            analysisMessageApi.info('AI-analyse kører i baggrunden. Du får besked her, når den er færdig.');
            return;
          }

          await loadLeadPolicies();

          if (result.analyzed_documents === 0) {
            message.info('Alle uploadede policer var allerede analyseret');
            return;
          }

          message.success(
            `AI-analyse færdig: ${result.analyzed_completed} gennemført, ${result.analyzed_failed} fejlede, ${result.skipped_completed} spranget over`
          );
        } catch (error: any) {
          console.error('Failed to analyze all policies', error);
          message.error(toPolicyUiErrorMessage(error, 'Kunne ikke analysere alle policer'));
        } finally {
          setPolicyAnalyzeAllLoading(false);
        }
      }
    });
  }, [analysisMessageApi, leadId, loadLeadPolicies, notCompletedPolicyCount, policyDocuments.length]);

  const updatePolicyDocument = useCallback((policyId: string, updater: (doc: LeadPolicyDocument) => LeadPolicyDocument) => {
    setPolicyDocuments((prev) => prev.map((doc) => (doc.id === policyId ? updater(doc) : doc)));
  }, []);

  const beginPolicyEdit = useCallback((doc: LeadPolicyDocument, edit?: LeadPolicyAnalysisEdit | null) => {
    const sourceEdit = edit ?? doc.analysis_edit ?? null;
    const payload = sourceEdit?.payload ? normalizeEditablePolicyPayload(sourceEdit.payload) : toEditablePayloadFromDocument(doc);
    const summary = sourceEdit?.summary ?? doc.analysis_summary ?? '';
    setPolicyEditDrafts((prev) => ({
      ...prev,
      [doc.id]: {
        summary,
        payload
      }
    }));
  }, []);

  const handleCreatePolicyCopy = useCallback(async (doc: LeadPolicyDocument) => {
    if (!leadId) return;
    setPolicyCopyCreatingById((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const data = await createEditablePolicyCopy(leadId, doc.id);
      if (!data.edit) {
        throw new Error('Kopi kunne ikke oprettes');
      }

      updatePolicyDocument(doc.id, (currentDoc) => ({
        ...currentDoc,
        analysis_edit: data.edit,
        effective_analysis_payload: (data.effective_analysis_payload || currentDoc.effective_analysis_payload || currentDoc.analysis_payload) as any,
        effective_analysis_summary: data.effective_analysis_summary ?? currentDoc.effective_analysis_summary ?? currentDoc.analysis_summary
      }));
      beginPolicyEdit(doc, data.edit);
      message.success('Redigerbar kopi oprettet');
    } catch (error: any) {
      console.error('Failed to create editable policy copy', error);
      message.error(error?.message || 'Kunne ikke oprette redigerbar kopi');
    } finally {
      setPolicyCopyCreatingById((prev) => {
        const next = { ...prev };
        delete next[doc.id];
        return next;
      });
    }
  }, [beginPolicyEdit, leadId, updatePolicyDocument]);

  const handleStartPolicyEdit = useCallback((doc: LeadPolicyDocument) => {
    beginPolicyEdit(doc, doc.analysis_edit ?? null);
  }, [beginPolicyEdit]);

  const handleCancelPolicyEdit = useCallback((policyId: string) => {
    setPolicyEditDrafts((prev) => {
      const next = { ...prev };
      delete next[policyId];
      return next;
    });
  }, []);

  const updatePolicyDraftSummary = useCallback((policyId: string, value: string) => {
    setPolicyEditDrafts((prev) => {
      const draft = prev[policyId];
      if (!draft) return prev;
      return {
        ...prev,
        [policyId]: {
          ...draft,
          summary: value
        }
      };
    });
  }, []);

  const updatePolicyDraftLine = useCallback(
    (policyId: string, section: PolicyEditableArrayKey, index: number, field: string, value: unknown) => {
      setPolicyEditDrafts((prev) => {
        const draft = prev[policyId];
        if (!draft) return prev;
        const source = Array.isArray((draft.payload as any)[section]) ? ([...(draft.payload as any)[section]] as Array<Record<string, unknown>>) : [];
        if (!source[index]) return prev;
        source[index] = {
          ...source[index],
          [field]: value
        };
        return {
          ...prev,
          [policyId]: {
            ...draft,
            payload: {
              ...draft.payload,
              [section]: source
            }
          }
        };
      });
    },
    []
  );

  const handleSavePolicyEdit = useCallback(async (doc: LeadPolicyDocument) => {
    if (!leadId) return;
    const draft = policyEditDrafts[doc.id];
    if (!draft) return;

    setPolicySavingById((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const data = await updateEditablePolicyCopy(leadId, doc.id, {
        summary: draft.summary?.trim() ? draft.summary.trim() : null,
        payload: draft.payload
      });

      if (!data.edit) {
        throw new Error('Kunne ikke gemme redigerbar kopi');
      }

      updatePolicyDocument(doc.id, (currentDoc) => ({
        ...currentDoc,
        analysis_edit: data.edit,
        effective_analysis_payload: (data.effective_analysis_payload || currentDoc.effective_analysis_payload || currentDoc.analysis_payload) as any,
        effective_analysis_summary: data.effective_analysis_summary ?? currentDoc.effective_analysis_summary ?? currentDoc.analysis_summary
      }));
      handleCancelPolicyEdit(doc.id);
      message.success('AI-kopi gemt');
    } catch (error: any) {
      console.error('Failed to save editable policy copy', error);
      message.error(error?.message || 'Kunne ikke gemme redigering');
    } finally {
      setPolicySavingById((prev) => {
        const next = { ...prev };
        delete next[doc.id];
        return next;
      });
    }
  }, [handleCancelPolicyEdit, leadId, policyEditDrafts, updatePolicyDocument]);

  const handleReanalyzePolicy = useCallback((doc: LeadPolicyDocument) => {
    if (!leadId) return;

    Modal.confirm({
      title: 'Kør ny AI-analyse?',
      content: 'Hvis der findes en redigerbar kopi, bruges den som grundlag for ny analyse.',
      okText: 'Kør ny analyse',
      cancelText: 'Annuller',
      okButtonProps: { danger: true },
      onOk: async () => {
        setPolicyReanalyzingById((prev) => ({ ...prev, [doc.id]: true }));
        try {
          await reanalyzePolicyDocument(leadId, doc.id);
          await loadLeadPolicies();
          message.success('Ny AI-analyse er gennemført');
        } catch (error: any) {
          console.error('Failed to reanalyze policy document', error);
          message.error(toPolicyUiErrorMessage(error, 'Kunne ikke køre ny AI analyse'));
        } finally {
          setPolicyReanalyzingById((prev) => {
            const next = { ...prev };
            delete next[doc.id];
            return next;
          });
        }
      }
    });
  }, [leadId, loadLeadPolicies]);

  const formatPolicyFileSize = useCallback((size?: number | null) => {
    if (!size) return 'Ukendt størrelse';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const formatPolicyAnalysisError = useCallback((rawError?: string | null) => {
    const errorText = typeof rawError === 'string' ? rawError.trim() : '';
    if (!errorText) {
      return 'Ukendt fejl under AI-analyse.';
    }

    if (/Policy analysis validation failed/i.test(errorText) || /kvalitetssikres automatisk/i.test(errorText)) {
      return 'AI-analysen blev oprettet, men svaret var for ufuldstændigt til sikker visning. Prøv "Ny AI-analyse" igen.';
    }

    return errorText;
  }, []);

  const cleanPolicySnippet = useCallback((rawValue: unknown) => {
    if (typeof rawValue !== 'string') {
      return '';
    }

    return rawValue
      .replace(/[\u201C\u201D\u00AB\u00BB"]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/([A-Za-zÆØÅæøå])(?=\d)/g, '$1 ')
      .replace(/\)(?=\d)/g, ') · ')
      .replace(/(\d{1,3}(?:\.\d{3})*,\d{2})(?=\d{1,3}(?:\.\d{3})*,\d{2})/g, '$1 · ')
      .replace(/\s*·\s*/g, ' · ')
      .trim();
  }, []);

  const formatCoverageNotes = useCallback((rawValue: unknown) => cleanPolicySnippet(rawValue), [cleanPolicySnippet]);
  const formatCoverageEvidence = useCallback((rawValue: unknown) => cleanPolicySnippet(rawValue), [cleanPolicySnippet]);

  const getPolicyStatusLabel = useCallback((doc: LeadPolicyDocument) => {
    if (doc.analysis_status === 'PENDING') {
      const isUnderAnalysis = Boolean(policyReanalyzingById[doc.id]) || policyAnalyzeAllBackground;
      return isUnderAnalysis ? 'Under analyse' : 'Uploaded';
    }

    return policyStatusLabels[doc.analysis_status] || doc.analysis_status;
  }, [policyAnalyzeAllBackground, policyReanalyzingById]);

  const renderPolicyAnalysisActions = useCallback((doc: LeadPolicyDocument) => (
    <PolicyAnalysisActions
      doc={doc}
      isEditing={Boolean(policyEditDrafts[doc.id])}
      policyAnalyzeAllBackground={policyAnalyzeAllBackground}
      policyCopyCreating={Boolean(policyCopyCreatingById[doc.id])}
      policySaving={Boolean(policySavingById[doc.id])}
      policyReanalyzing={Boolean(policyReanalyzingById[doc.id])}
      onCreateCopy={handleCreatePolicyCopy}
      onStartEdit={handleStartPolicyEdit}
      onSaveEdit={handleSavePolicyEdit}
      onCancelEdit={handleCancelPolicyEdit}
      onPdfExport={handlePolicyPdfExport}
      onReanalyze={handleReanalyzePolicy}
    />
  ), [
    handleCancelPolicyEdit,
    handleCreatePolicyCopy,
    handlePolicyPdfExport,
    handleReanalyzePolicy,
    handleSavePolicyEdit,
    handleStartPolicyEdit,
    policyAnalyzeAllBackground,
    policyCopyCreatingById,
    policyEditDrafts,
    policyReanalyzingById,
    policySavingById
  ]);

  const handleCreatePolicyFollowUp = useCallback(
    async (title: string, description: string) => {
      if (!leadId) {
        return;
      }

      try {
        const todo = await createLeadTodo(leadId, {
          title,
          description,
          status: 'NEW',
          priority: 'MEDIUM',
          due_at: null,
          assigned_to: null,
          sync_to_calendar: false
        });
        setTodos((prev) => [todo, ...prev]);
        message.success('Opgave oprettet');
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette opgave');
      }
    },
    [leadId]
  );

  const handleCreatePolicyFollowUpBatch = useCallback(
    async (items: Array<{ title: string; description: string }>) => {
      if (!leadId || !items.length) {
        return;
      }

      try {
        for (const item of items) {
          const todo = await createLeadTodo(leadId, {
            title: item.title,
            description: item.description,
            status: 'NEW',
            priority: 'MEDIUM',
            due_at: null,
            assigned_to: null,
            sync_to_calendar: false
          });
          setTodos((prev) => [todo, ...prev]);
        }
        message.success(`Oprettede ${items.length} opgaver`);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette opgaver');
      }
    },
    [leadId]
  );

  const renderPolicyAnalysis = useCallback((doc: LeadPolicyDocument) => (
    <PolicyAnalysisDetails
      doc={doc}
      draft={policyEditDrafts[doc.id]}
      formatCoverageEvidence={formatCoverageEvidence}
      formatCoverageNotes={formatCoverageNotes}
      formatPolicyAnalysisError={formatPolicyAnalysisError}
      onCreateFollowUp={handleCreatePolicyFollowUp}
      onCreateFollowUpBatch={handleCreatePolicyFollowUpBatch}
      onUpdateDraftLine={updatePolicyDraftLine}
      onUpdateDraftSummary={updatePolicyDraftSummary}
    />
  ), [
    handleCreatePolicyFollowUp,
    handleCreatePolicyFollowUpBatch,
    formatCoverageEvidence,
    formatCoverageNotes,
    formatPolicyAnalysisError,
    policyEditDrafts,
    updatePolicyDraftLine,
    updatePolicyDraftSummary
  ]);

  const loadDetail = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      setCvrSummary(null);
      const res = await api(`/api/leads/${leadId}/detail`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente lead');
      }
      const data = (await res.json()) as LeadDetailResponse;
      setLead(data.lead);
      setEvents(data.events || []);
      setMeetings(data.meetings || []);
      setLeadLabels(
        Array.isArray(data.labels)
          ? [...data.labels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
          : []
      );
      const summary = data.cvrSummary ?? null;
      setCvrSummary(summary);
      setInitialCvrSummary(summary);
      setCvrLookupError(null);
      setCvrLookupLoading(false);
      notesForm.setFieldsValue({
        notes: data.lead?.notes || '',
        cvrnummer: data.lead?.cvrnummer || '',
        contact_name: data.lead?.contact_name || '',
        contact_email: data.lead?.contact_email || '',
        contact_phone: data.lead?.contact_phone || ''
      });
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente lead');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [leadId, notesForm, onBack]);

  const loadLeadGroup = useCallback(async (groupId: string | null) => {
    if (!groupId) {
      setLeadGroup(null);
      return;
    }
    try {
      setLeadGroupLoading(true);
      const res = await api(`/api/lead-groups/${groupId}`);
      if (res.status === 404) {
        setLeadGroup(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente samlekunde');
      }
      setLeadGroup({
        ...(data.group as LeadGroup),
        members: Array.isArray(data.members) ? (data.members as LeadGroupMember[]) : []
      });
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente samlekunde');
    } finally {
      setLeadGroupLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (lead?.lead_group_id) {
      void loadLeadGroup(lead.lead_group_id as string);
    } else {
      setLeadGroup(null);
    }
  }, [lead?.lead_group_id, loadLeadGroup]);

  useEffect(() => {
    void loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    void loadTodoAssignees();
  }, [loadTodoAssignees]);

  useEffect(() => {
    return () => {
      if (leadSearchTimeoutRef.current) {
        window.clearTimeout(leadSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!canManage) {
      setAllLabels([]);
      setSelectedLabelId(null);
      setLabelsLoading(false);
      return;
    }

    let aborted = false;
    setLabelsLoading(true);

    (async () => {
      try {
        const res = await api('/api/labels');
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente labels');
        }
        const data = (await res.json()) as Label[];
        if (aborted) {
          return;
        }
        setAllLabels(
          [...data].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
        );
      } catch (error: any) {
        if (!aborted) {
          console.error('Failed to load labels', error);
          message.error(error?.message || 'Kunne ikke hente labels');
        }
      } finally {
        if (!aborted) {
          setLabelsLoading(false);
        }
      }
    })();

    return () => {
      aborted = true;
    };
  }, [canManage]);

  useEffect(() => {
    if (!canManage) {
      return;
    }

    const raw = typeof watchedCvrValue === 'string' ? watchedCvrValue : '';
    const digits = raw.replace(/\D+/g, '');
    if (!digits) {
      setCvrLookupLoading(false);
      setCvrLookupError(null);
      setCvrSummary(initialCvrSummary);
      notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    if (digits.length !== 8) {
      const errMsg = 'CVR-nummer skal bestå af 8 cifre';
      setCvrLookupLoading(false);
      setCvrSummary(null);
      setCvrLookupError(errMsg);
      notesForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
      return;
    }

    if (digits === savedLeadCvr && initialCvrSummary) {
      setCvrSummary(initialCvrSummary);
      setCvrLookupLoading(false);
      setCvrLookupError(null);
      notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
    const lookupId = ++cvrLookupSeq.current;
    setCvrLookupLoading(true);
    setCvrLookupError(null);

    (async () => {
      try {
        const res = await api(`/api/leads/lookup/cvr/${digits}`);
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }

        if (!res.ok) {
          let errMsg = 'Kunne ikke slå CVR op';
          if (res.status === 404) {
            errMsg = 'Ingen virksomhed fundet for CVR-nummeret';
          } else if (res.status === 400) {
            errMsg = 'CVR-nummer skal bestå af 8 cifre';
          }
          setCvrSummary(null);
          setCvrLookupError(errMsg);
          notesForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
          return;
        }

        const data = (await res.json()) as CvrSummary;
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }

        setCvrSummary(data);
        setCvrLookupError(null);
      } catch (error) {
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }
        console.error('CVR lookup failed', error);
        const errMsg = 'Kunne ikke slå CVR op';
        setCvrSummary(null);
        setCvrLookupError(errMsg);
        message.error(errMsg);
      } finally {
        if (lookupId === cvrLookupSeq.current) {
          setCvrLookupLoading(false);
        }
      }
    })();
  }, [canManage, watchedCvrValue, notesForm, initialCvrSummary, savedLeadCvr]);

  const handleSaveNotes = async () => {
    if (!leadId) return;
    try {
      const values = await notesForm.validateFields();
      setSavingNotes(true);
      const sanitizedCvr = typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      if (sanitizedCvr && sanitizedCvr.length !== 8) {
        message.error('CVR-nummer skal bestå af 8 cifre');
        setSavingNotes(false);
        return;
      }
      const contactName = typeof values.contact_name === 'string' ? values.contact_name.trim() : '';
      const contactEmailRaw = typeof values.contact_email === 'string' ? values.contact_email.trim() : '';
      const contactPhone = typeof values.contact_phone === 'string' ? values.contact_phone.trim() : '';
      let contactEmail: string | null = null;
      if (contactEmailRaw) {
        const lower = contactEmailRaw.toLowerCase();
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(lower)) {
          message.error('Ugyldig e-mailadresse');
          setSavingNotes(false);
          return;
        }
        contactEmail = lower;
      }
      const res = await api(`/api/leads/${leadId}/detail`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: values.notes ?? '',
          cvrnummer: sanitizedCvr ? sanitizedCvr : null,
          contact_name: contactName || null,
          contact_email: contactEmailRaw ? contactEmail : null,
          contact_phone: contactPhone || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere noter');
      }
      await res.json().catch(() => null);
      message.success('Detaljer opdateret');
      await loadDetail();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere noter');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleOpenPolicyInsuranceModal = useCallback((policyId: string) => {
    const currentItems = policyInsuranceTypesById[policyId] || [];
    setPolicyInsuranceModalPolicyId(policyId);
    setSelectedPolicyInsuranceTypeIds(currentItems.map((item) => String(item.insuranceTypeId)));
    setPolicyInsuranceModalOpen(true);
  }, [policyInsuranceTypesById]);

  const handleClosePolicyInsuranceModal = useCallback(() => {
    setPolicyInsuranceModalOpen(false);
    setPolicyInsuranceModalPolicyId(null);
    setSelectedPolicyInsuranceTypeIds([]);
  }, []);

  const handleTogglePolicyInsuranceType = useCallback((id: string, checked: boolean) => {
    setSelectedPolicyInsuranceTypeIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return Array.from(next);
    });
  }, []);

  const handleSavePolicyInsuranceTypes = useCallback(async () => {
    if (!leadId || !canManage || !policyInsuranceModalPolicyId) {
      return;
    }
    setPolicyInsuranceModalSaving(true);
    try {
      const data = await replacePolicyInsuranceTypes(leadId, policyInsuranceModalPolicyId, selectedPolicyInsuranceTypeIds);
      const normalized = normalizePolicyInsuranceItems(data.items || []);
      setPolicyInsuranceTypesById((prev) => ({
        ...prev,
        [policyInsuranceModalPolicyId]: normalized
      }));

      message.success('Forsikringstyper opdateret');
      if (data.editableCopyCreated) {
        message.success('Redigerbar kopi blev automatisk oprettet for policen');
      }
      if (data.editableCopyCreated || data.editableCopyUpdated) {
        void loadLeadPolicies();
      }
      handleClosePolicyInsuranceModal();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere forsikringstyper');
    } finally {
      setPolicyInsuranceModalSaving(false);
    }
  }, [
    canManage,
    handleClosePolicyInsuranceModal,
    leadId,
    loadLeadPolicies,
    normalizePolicyInsuranceItems,
    onLeadUpdated,
    policyInsuranceModalPolicyId,
    selectedPolicyInsuranceTypeIds
  ]);

  const handleRemovePolicyInsuranceType = useCallback(async (policyId: string, insuranceTypeId: string) => {
    if (!leadId || !canManage) {
      return;
    }
    const key = `${policyId}:${insuranceTypeId}`;
    setRemovingPolicyInsuranceKey(key);
    try {
      const currentItems = policyInsuranceTypesById[policyId] || [];
      const remainingIds = currentItems
        .map((item) => String(item.insuranceTypeId))
        .filter((id) => id !== insuranceTypeId);
      const data = await replacePolicyInsuranceTypes(leadId, policyId, remainingIds);
      const normalized = normalizePolicyInsuranceItems(data.items || []);
      setPolicyInsuranceTypesById((prev) => ({
        ...prev,
        [policyId]: normalized
      }));
      message.success('Forsikringstype fjernet');
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke fjerne forsikringstype');
    } finally {
      setRemovingPolicyInsuranceKey(null);
    }
  }, [canManage, leadId, normalizePolicyInsuranceItems, onLeadUpdated, policyInsuranceTypesById]);

  const handleAttachLabel = useCallback(async () => {
    if (!leadId || !selectedLabelId || !canManage) {
      return;
    }
    try {
      setAttachLoading(true);
      const res = await api(`/api/leads/${leadId}/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labelId: selectedLabelId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke tilknytte label');
      }
      const attached = (await res.json()) as Label;
      setLeadLabels((prev) => {
        const exists = prev.some((item) => item.id === attached.id);
        const next = exists
          ? prev.map((item) => (item.id === attached.id ? attached : item))
          : [...prev, attached];
        return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
      });
      setAllLabels((prev) => {
        const exists = prev.some((item) => item.id === attached.id);
        const next = exists ? prev.map((item) => (item.id === attached.id ? attached : item)) : [...prev, attached];
        return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
      });
      setSelectedLabelId(null);
      message.success('Label tilknyttet');
      if (onLeadUpdated) {
        await onLeadUpdated(leadId);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke tilknytte label');
    } finally {
      setAttachLoading(false);
    }
  }, [canManage, leadId, onLeadUpdated, selectedLabelId]);

  const handleDetachLabel = useCallback(
    async (label: Label) => {
      if (!leadId || !canManage) {
        return;
      }
      setRemovingLabelId(label.id);
      try {
        const res = await api(`/api/leads/${leadId}/labels/${label.id}`, {
          method: 'DELETE'
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke fjerne label');
        }
        const data = (await res.json().catch(() => null)) as { labelId: string; leadCount: number } | null;
        setLeadLabels((prev) => prev.filter((item) => item.id !== label.id));
        if (data && typeof data.leadCount === 'number') {
          setAllLabels((prev) => {
            const exists = prev.some((item) => item.id === label.id);
            if (!exists) {
              return prev;
            }
            const next = prev.map((item) =>
              item.id === label.id ? { ...item, leadCount: data.leadCount } : item
            );
            return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
          });
        }
        message.success('Label fjernet');
        if (onLeadUpdated) {
          await onLeadUpdated(leadId);
        }
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke fjerne label');
      } finally {
        setRemovingLabelId(null);
      }
    },
    [canManage, leadId, onLeadUpdated]
  );

  const handleCreateTodoDrawer = useCallback(() => {
    todoForm.setFieldsValue({
      title: '',
      description: '',
      status: 'NEW',
      priority: 'MEDIUM',
      due_at: null,
      assigned_to: null,
      sync_to_calendar: false
    });
    setEditingTodo(null);
    setTodoDrawerOpen(true);
  }, [todoForm]);

  const handleEditTodoDrawer = useCallback(
    (todo: LeadTodo) => {
      todoForm.setFieldsValue({
        title: todo.title,
        description: todo.description || '',
        status: todo.status,
        priority: todo.priority,
        due_at: todo.due_at ? dayjs(todo.due_at) : null,
        assigned_to: todo.assigned_to || null,
        sync_to_calendar: todo.sync_to_calendar
      });
      setEditingTodo(todo);
      setTodoDrawerOpen(true);
    },
    [todoForm]
  );

  const handleTodoDrawerClose = useCallback(() => {
    setTodoDrawerOpen(false);
    setEditingTodo(null);
    todoForm.resetFields();
  }, [todoForm]);

  const handleTodoFormSubmit = useCallback(
    async (values: TodoFormValues) => {
      if (!leadId) {
        return;
      }

      const title = typeof values.title === 'string' ? values.title.trim() : '';
      if (!title) {
        message.error('Titel skal udfyldes');
        return;
      }

      const descriptionValue =
        typeof values.description === 'string' ? values.description.trim() : '';

      const dueValue = values.due_at ? values.due_at.toDate().toISOString() : null;

      const payload: LeadTodoPayload = {
        title,
        description: descriptionValue ? descriptionValue : null,
        status: values.status,
        priority: values.priority,
        due_at: dueValue,
        assigned_to: values.assigned_to ? values.assigned_to : null,
        sync_to_calendar: Boolean(values.sync_to_calendar)
      };

      if (payload.sync_to_calendar && !payload.due_at) {
        message.error('Tilføj en forfaldsdato for at synkronisere til kalenderen');
        return;
      }

      try {
        setSavingTodo(true);
        if (editingTodo) {
          await updateLeadTodo(leadId, editingTodo.id, payload);
          message.success('Opgave opdateret');
        } else {
          await createLeadTodo(leadId, payload);
          message.success('Opgave oprettet');
        }
        handleTodoDrawerClose();
        await loadTodos();
        await onLeadUpdated?.(leadId);
      } catch (error: any) {
        if (error?.errorFields) {
          return;
        }
        message.error(error?.message || 'Kunne ikke gemme opgave');
      } finally {
        setSavingTodo(false);
      }
    },
    [editingTodo, handleTodoDrawerClose, leadId, loadTodos, onLeadUpdated]
  );

  const handleOpenTodoHistory = useCallback(
    async (todo: LeadTodo) => {
      if (!leadId) {
        return;
      }
      setHistoryTodo(todo);
      setTodoHistory([]);
      setTodoHistoryModalOpen(true);
      try {
        setTodoHistoryLoading(true);
        const data = await fetchLeadTodoEvents(leadId, todo.id);
        setTodoHistory(data);
      } catch (error: any) {
        console.error('Failed to load todo history', error);
        message.error(error?.message || 'Kunne ikke hente historik');
      } finally {
        setTodoHistoryLoading(false);
      }
    },
    [leadId]
  );

  const handleTodoHistoryClose = useCallback(() => {
    setTodoHistoryModalOpen(false);
    setHistoryTodo(null);
    setTodoHistory([]);
  }, []);

  const formatAssigneeLabel = useCallback((todo: LeadTodo) => {
    if (todo.assigned_to_name && todo.assigned_to_name.trim()) {
      return todo.assigned_to_name.trim();
    }
    if (todo.assigned_to_email) {
      return todo.assigned_to_email;
    }
    return 'Ingen ansvarlig';
  }, []);

  const handleRegisterCallAttempt = useCallback(async () => {
    if (!leadId || !canManage) {
      return;
    }
    try {
      setCallAttemptLoading(true);
      const res = await api(`/api/leads/${leadId}/call-attempt`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke registrere opkald');
      }
      const data = (await res.json().catch(() => ({}))) as {
        callAttemptCount?: number;
        lastCallAt?: string;
      } | null;
      const attempts = Math.max(1, Number(data?.callAttemptCount ?? callAttemptCount + 1));
      const lastCallDisplay = data?.lastCallAt ? formatDateTimeShort(data.lastCallAt) : null;
      const successText = lastCallDisplay
        ? `Opkald registreret (forsøg #${attempts}) · senest ${lastCallDisplay}`
        : `Opkald registreret (forsøg #${attempts})`;
      message.success(successText);
      await loadDetail();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke registrere opkald');
    } finally {
      setCallAttemptLoading(false);
    }
  }, [canManage, callAttemptCount, leadId, loadDetail, onLeadUpdated]);

  const handleVisibilityChange = useCallback(
    async (hidden: boolean) => {
      if (!leadId || !canManage) {
        return;
      }
      try {
        setVisibilityLoading(true);
        const res = await api(`/api/leads/${leadId}/visibility`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hidden })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke opdatere synlighed');
        }
        await res.json().catch(() => null);
        message.success(hidden ? 'Lead skjult' : 'Lead gjort synlig');
        await loadDetail();
        await onLeadUpdated?.(leadId);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere synlighed');
      } finally {
        setVisibilityLoading(false);
      }
    },
    [canManage, leadId, loadDetail, onLeadUpdated]
  );

  const handleCreateMeeting = useCallback(() => {
    meetingForm.resetFields();
    setEditingMeeting(null);
    setMeetingDrawerOpen(true);
  }, [meetingForm]);

  const handleEditMeeting = useCallback(
    (meeting: LeadMeeting) => {
      meetingForm.setFieldsValue({
        scheduled_for: toLocalInputValue(meeting.scheduled_for),
        location: meeting.location ?? undefined,
        summary: meeting.summary ?? undefined
      });
      setEditingMeeting(meeting);
      setMeetingDrawerOpen(true);
    },
    [meetingForm]
  );

  const handleMeetingDrawerClose = useCallback(() => {
    setMeetingDrawerOpen(false);
    setEditingMeeting(null);
    meetingForm.resetFields();
  }, [meetingForm]);

  const handleMeetingSubmit = useCallback(
    async (values: { scheduled_for?: string; location?: string; summary?: string }) => {
      if (!leadId) {
        return;
      }
      try {
        setSavingMeeting(true);
        const payload = {
          scheduled_for: values.scheduled_for ? new Date(values.scheduled_for).toISOString() : null,
          location: values.location ? values.location.trim() || null : null,
          summary: values.summary ? values.summary.trim() || null : null
        };
        const endpoint = editingMeeting
          ? `/api/leads/${leadId}/meetings/${editingMeeting.id}`
          : `/api/leads/${leadId}/meetings`;
        const method = editingMeeting ? 'PATCH' : 'POST';

        const res = await api(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke gemme møde');
        }

        message.success(editingMeeting ? 'Møde opdateret' : 'Møde oprettet');
        await loadDetail();
        meetingForm.resetFields();
        setEditingMeeting(null);
        setMeetingDrawerOpen(false);
      } catch (error: any) {
        if (error?.errorFields) {
          return;
        }
        message.error(error?.message || 'Kunne ikke gemme møde');
      } finally {
        setSavingMeeting(false);
      }
    },
    [editingMeeting, leadId, loadDetail, meetingForm]
  );

  const handleDeleteMeeting = useCallback(
    (meeting: LeadMeeting) => {
      if (!leadId) {
        return;
      }
      Modal.confirm({
        title: 'Slet møde',
        content: 'Er du sikker på, at du vil slette dette møde?',
        okText: 'Slet',
        okType: 'danger',
        cancelText: 'Annuller',
        async onOk() {
          setDeletingMeetingId(meeting.id);
          try {
            const res = await api(`/api/leads/${leadId}/meetings/${meeting.id}`, { method: 'DELETE' });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || 'Kunne ikke slette møde');
            }
            message.success('Møde slettet');
            await loadDetail();
          } catch (error: any) {
            message.error(error?.message || 'Kunne ikke slette møde');
          } finally {
            setDeletingMeetingId(null);
          }
        }
      });
    },
    [leadId, loadDetail]
  );

  const meetingDrawerTitle = editingMeeting ? 'Rediger møde' : 'Planlæg møde';
  const meetingDrawerSubmitLabel = editingMeeting ? 'Opdater møde' : 'Gem møde';
  const isLinkingExistingGroup = linkModalMode === 'add';
  const leadSearchEmptyText = leadSearchLoading
    ? 'Søger…'
    : leadSearchQuery.length >= 2 && leadSearchResults.length === 0
      ? 'Ingen leads fundet'
      : 'Skriv mindst 2 tegn for at søge';

  const openLinkModal = useCallback(
    (mode: 'create' | 'add') => {
      if (mode === 'add' && !leadGroup?.id) {
        return;
      }
      setLinkModalMode(mode);
      setSelectedLinkLeadId(null);
      setLeadSearchResults([]);
      setLeadSearchQuery('');
      setLeadSearchLoading(false);
      setLinkModalOpen(true);
    },
    [leadGroup?.id]
  );

  const closeLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setSelectedLinkLeadId(null);
    setLeadSearchResults([]);
    setLeadSearchQuery('');
    setLeadSearchLoading(false);
    if (leadSearchTimeoutRef.current) {
      window.clearTimeout(leadSearchTimeoutRef.current);
      leadSearchTimeoutRef.current = null;
    }
  }, []);

  const handleLeadSearch = useCallback(
    (value: string) => {
      if (leadSearchTimeoutRef.current) {
        window.clearTimeout(leadSearchTimeoutRef.current);
        leadSearchTimeoutRef.current = null;
      }
      const trimmed = value.trim();
      setLeadSearchQuery(trimmed);
      if (trimmed.length < 2) {
        setLeadSearchResults([]);
        setLeadSearchLoading(false);
        return;
      }
      leadSearchTimeoutRef.current = window.setTimeout(async () => {
        leadSearchTimeoutRef.current = null;
        setLeadSearchLoading(true);
        try {
          const params = new URLSearchParams();
          params.set('q', trimmed);
          params.set('includeHidden', 'true');
          const res = await api(`/api/leads?${params.toString()}`);
          const payload = await res.json().catch(() => []);
          if (!res.ok) {
            throw new Error((payload as any)?.error || 'Kunne ikke søge efter leads');
          }
          const rows = Array.isArray(payload) ? payload : [];
          const excluded = new Set<string>();
          if (lead?.id) {
            excluded.add(lead.id);
          }
          if (leadGroup?.members) {
            for (const member of leadGroup.members) {
              excluded.add(member.id);
            }
          }
          setLeadSearchResults(
            rows
              .filter((row: any) => !excluded.has(row.id))
              .map((row: any) => ({
                id: row.id,
                company_name: row.company_name,
                cvrnummer: row.cvrnummer ?? null,
                status: row.status
              }))
          );
        } catch (error: any) {
          console.error('Failed to search leads', error);
          message.error(error?.message || 'Kunne ikke søge efter leads');
        } finally {
          setLeadSearchLoading(false);
        }
      }, 300);
    },
    [lead?.id, leadGroup]
  );

  const handleLinkSubmit = useCallback(async () => {
    if (!leadId || !selectedLinkLeadId) {
      return;
    }
    setLinkModalSubmitting(true);
    try {
      const headers = new Headers({ 'Content-Type': 'application/json' });
      let endpoint = '/api/lead-groups';
      let body: Record<string, unknown> = { lead_ids: [leadId, selectedLinkLeadId] };
      if (linkModalMode === 'add' && leadGroup?.id) {
        endpoint = `/api/lead-groups/${leadGroup.id}/leads`;
        body = { lead_ids: [selectedLinkLeadId] };
      }
      const res = await api(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as any)?.error || 'Kunne ikke linke lead');
      }
      const targetGroupId = (payload as any)?.group?.id ?? leadGroup?.id ?? null;
      message.success(
        linkModalMode === 'add' && leadGroup?.id
          ? 'Lead tilføjet til samlingen'
          : 'Samlekunde oprettet'
      );
      closeLinkModal();
      await loadDetail();
      if (targetGroupId) {
        await loadLeadGroup(targetGroupId);
      } else {
        setLeadGroup(null);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke linke lead');
    } finally {
      setLinkModalSubmitting(false);
    }
  }, [leadId, selectedLinkLeadId, linkModalMode, leadGroup, closeLinkModal, loadDetail, loadLeadGroup]);

  const handleRemoveMember = useCallback(
    (memberId: string) => {
      if (!leadGroup?.id) {
        return;
      }
      Modal.confirm({
        title: 'Fjern lead fra samling',
        content: 'Er du sikker på, at du vil fjerne dette lead fra samlingen?',
        okText: 'Fjern',
        okType: 'danger',
        cancelText: 'Annuller',
        async onOk() {
          setRemovingGroupMemberId(memberId);
          try {
            const res = await api(`/api/lead-groups/${leadGroup.id}/leads/${memberId}`, {
              method: 'DELETE'
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              throw new Error((payload as any)?.error || 'Kunne ikke fjerne lead');
            }
            message.success('Lead fjernet fra samlingen');
            await loadDetail();
            if ((payload as any)?.group?.id) {
              await loadLeadGroup((payload as any).group.id);
            } else {
              setLeadGroup(null);
            }
          } catch (error: any) {
            message.error(error?.message || 'Kunne ikke fjerne lead');
          } finally {
            setRemovingGroupMemberId(null);
          }
        }
      });
    },
    [leadGroup?.id, loadDetail, loadLeadGroup]
  );

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {analysisMessageContextHolder}
      <Button
        onClick={() =>
          onBack(backLocationState)
        }
      >
        Tilbage til oversigt
      </Button>

      <Card loading={loading} title={lead?.company_name ?? 'Lead'}>
        {lead && (
          <>
            <Space size={8} wrap style={{ marginBottom: 16 }}>
              <Tag color={isHidden ? 'default' : 'blue'}>
                {isHidden ? 'Skjult lead' : 'Synligt lead'}
              </Tag>
              <Tooltip title={callAttemptCount > 0 ? lastCallFull || undefined : undefined}>
                <Tag color={callAttemptCount > 0 ? 'processing' : 'default'}>
                  Opkald: {callAttemptCount}
                  {callAttemptCount > 0 && lastCallShort ? ` · senest ${lastCallShort}` : ''}
                </Tag>
              </Tooltip>
              <Tooltip title={tierDescription || undefined}>
                <Tag color={tierTagColor}>{tierLabel}</Tag>
              </Tooltip>
              {canManage && (
                <>
                  <Button onClick={handleRegisterCallAttempt} loading={callAttemptLoading}>
                    Registrer opkald uden svar
                  </Button>
                  <Button
                    onClick={() => handleVisibilityChange(!isHidden)}
                    loading={visibilityLoading}
                  >
                    {isHidden ? 'Markér som synlig' : 'Skjul lead'}
                  </Button>
                </>
              )}
            </Space>
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" size={8}>
                  <div>
                    <Text strong>Status:</Text>{' '}
                    <Tag color={statusTagColors[lead.status] || 'default'}>
                      {statusLabelMap.get(lead.status) || lead.status}
                    </Tag>
                  </div>
                  <div>
                    <Text strong>CVR-nummer:</Text>{' '}
                    {formattedCvr || '—'}
                  </div>
                  <div>
                    <Text strong>Ansatte (seneste år):</Text>{' '}
                    {employeesDisplay ?? '—'}
                    {employeesYear ? ` (${employeesYear})` : ''}
                  </div>
                  <div>
                    <Text strong>Kontraktværdi:</Text>{' '}
                    {formatAmount(lead.contract_value_annual, lead.currency)}
                  </div>
                  <div>
                    <Text strong>Provision:</Text>{' '}
                    {commissionDisplay}
                  </div>
                  <div>
                    <Text strong>Referrer:</Text>{' '}
                    {lead.ref_full_name || lead.ref_email || 'Ukendt'}
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size={8}>
                  <div>
                    <Text strong>Kontaktperson:</Text>{' '}
                    {lead.contact_name || '—'}
                  </div>
                  <div>
                    <Text strong>E-mail:</Text>{' '}
                    {lead.contact_email ? (
                      <a href={`mailto:${lead.contact_email}`}>{lead.contact_email}</a>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div>
                    <Text strong>Telefon:</Text>{' '}
                    {lead.contact_phone || '—'}
                  </div>
                  <div>
                    <Text strong>Sidst opdateret:</Text>{' '}
                    {formatDateTime(lead.last_updated)}
                  </div>
                </Space>
              </Col>
            </Row>
            <Divider />
          </>
        )}
      </Card>

      {leadId && (
        <Card title="Policedokumenter og samlet eksport" loading={policyLoading}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size={12} wrap style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space size={8} wrap>
                <Text strong>Samlet eksport</Text>
                <Text type="secondary">{completedPolicyCount} analyserede dokumenter klar</Text>
              </Space>
              <Space size={8} wrap>
                <Select
                  value={combinedPolicyExportType}
                  style={{ minWidth: 220 }}
                  onChange={(value) => setCombinedPolicyExportType(value as PolicyExportType)}
                  options={[
                    { value: 'tender', label: 'Udbudsmateriale (PDF)' },
                    { value: 'customer-agreement', label: 'Aftaledokument (PDF)' }
                  ]}
                />
                <Button
                  onClick={handleCombinedPolicyPdfExport}
                  loading={combinedPolicyExportLoading}
                  disabled={!leadId || completedPolicyCount === 0 || combinedPolicyExportLoading}
                >
                  Eksportér samlet PDF
                </Button>
                <Button
                  onClick={handleCombinedPolicyExcelExport}
                  loading={combinedPolicyExcelExportLoading}
                  disabled={!leadId || completedPolicyCount === 0 || combinedPolicyExcelExportLoading}
                >
                  Eksportér samlet Excel (skabelon)
                </Button>
              </Space>
            </Space>
            <Text type="secondary">
              Excel-eksporten udfylder skabelonen Udbudsmateriale DK.xlsx automatisk.
            </Text>

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Upload
                multiple
                fileList={policyFileList}
                beforeUpload={(file) => {
                  const maxSize = 25 * 1024 * 1024;
                  if (file.size > maxSize) {
                    message.error(`${file.name} er større end 25 MB`);
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => {
                  const limited = fileList.slice(-6);
                  if (fileList.length > limited.length) {
                    message.warning('Der kan max uploades 6 filer ad gangen');
                  }
                  setPolicyFileList(limited);
                }}
              >
                <Button icon={<UploadOutlined />}>Vælg dokumenter</Button>
              </Upload>
              <Space size={12} wrap>
                <Button
                  type="primary"
                  onClick={handlePolicyUpload}
                  loading={policyUploading}
                  disabled={!leadId || policyFileList.length === 0}
                >
                  Upload
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleAnalyzeAllPolicies}
                  loading={policyAnalyzeAllLoading}
                  disabled={!leadId || policyDocuments.length === 0 || policyAnalyzeAllBackground}
                >
                  AI-analyser alle uploadede
                </Button>
                {policyAnalyzeAllLoading ? <Tag color="processing">AI-analyse igang...</Tag> : null}
                {policyAnalyzeAllBackground ? (
                  <Tag color="processing">
                    AI-analyse i baggrunden{pendingPolicyCount > 0 ? ` (${pendingPolicyCount} under analyse)` : ''}
                  </Tag>
                ) : null}
                {!policyAnalyzeAllLoading ? (
                  <Text type="secondary">{notCompletedPolicyCount} dokumenter mangler analyse</Text>
                ) : null}
                <Text type="secondary">PDF/DOCX/TXT · max 25 MB pr. fil</Text>
              </Space>
              {canManage && !hideInsurance && availableInsuranceTypes.length === 0 && !insuranceTypesLoading ? (
                <Text type="secondary">
                  Opret forsikringstyper under Tilpasning for at tilføje dem til policer.
                </Text>
              ) : null}
            </Space>

            {policyDocuments.length === 0 ? (
              <Text type="secondary">Ingen dokumenter i udbudsværkstedet endnu.</Text>
            ) : (
              <List
                dataSource={policyDocuments}
                renderItem={(doc) => (
                  <List.Item
                    key={doc.id}
                    actions={[
                      <Button key="view-original" size="small" onClick={() => handlePolicyViewOriginal(doc)}>
                        Vis original
                      </Button>,
                      <Button key="download" size="small" onClick={() => handlePolicyDownload(doc)}>
                        Download
                      </Button>,
                      canManage ? (
                        <Button
                          key="delete"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeletePolicy(doc)}
                        >
                          Slet
                        </Button>
                      ) : null
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space size={8} wrap>
                          <Text strong>{doc.file_name}</Text>
                          <Tag color={policyStatusColors[doc.analysis_status] || 'default'}>
                            {getPolicyStatusLabel(doc)}
                          </Tag>
                          {doc.analysis_model ? <Tag>{doc.analysis_model}</Tag> : null}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Text type="secondary">
                            Uploadet {formatDateTimeShort(doc.uploaded_at)} · {formatPolicyFileSize(doc.file_size)}
                          </Text>
                          {!hideInsurance ? (
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                              <Space size={8} wrap>
                                <Text strong>Forsikringstyper</Text>
                                {canManage ? (
                                  <Button
                                    icon={<PlusOutlined />}
                                    size="small"
                                    onClick={() => handleOpenPolicyInsuranceModal(doc.id)}
                                    disabled={!leadId || availableInsuranceTypes.length === 0 || insuranceTypesLoading}
                                  >
                                    Tilføj / rediger
                                  </Button>
                                ) : null}
                              </Space>

                              {policyInsuranceLoadingById[doc.id] ? (
                                <Text type="secondary">Henter forsikringstyper...</Text>
                              ) : (policyInsuranceTypesById[doc.id] || []).length === 0 ? (
                                <Text type="secondary">Ingen forsikringstyper tilknyttet denne police.</Text>
                              ) : (
                                <List
                                  size="small"
                                  dataSource={policyInsuranceTypesById[doc.id] || []}
                                  renderItem={(item) => (
                                    <List.Item
                                      key={`${doc.id}-${item.id}`}
                                      actions={
                                        canManage
                                          ? [
                                              <Button
                                                key="remove"
                                                type="text"
                                                danger
                                                size="small"
                                                onClick={() => handleRemovePolicyInsuranceType(doc.id, String(item.insuranceTypeId))}
                                                loading={removingPolicyInsuranceKey === `${doc.id}:${String(item.insuranceTypeId)}`}
                                              >
                                                Fjern
                                              </Button>
                                            ]
                                          : undefined
                                      }
                                    >
                                      <List.Item.Meta
                                        title={
                                          <Space size={8} wrap>
                                            <Text strong>{item.title}</Text>
                                            {item.category ? <Tag>{item.category}</Tag> : null}
                                            {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                                          </Space>
                                        }
                                        description={
                                          item.description ? (
                                            <Text>{item.description}</Text>
                                          ) : (
                                            <Text type="secondary">Ingen beskrivelse</Text>
                                          )
                                        }
                                      />
                                    </List.Item>
                                  )}
                                />
                              )}
                            </Space>
                          ) : null}
                          {policyTemplateLoadingById[doc.id] ? (
                            <Text type="secondary">Henter relevante udbudsskabeloner...</Text>
                          ) : policyTemplatesById[doc.id] ? (
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Text strong>Udbudsskabelon</Text>
                              {policyTemplatesById[doc.id].policyInsuranceTypes.length === 0 ? (
                                <Text type="secondary">Tilknyt forsikringstyper til policen, før en udbudsskabelon kan vælges.</Text>
                              ) : policyTemplatesById[doc.id].templates.length === 0 ? (
                                <Text type="secondary">Ingen aktiv og analyseret udbudsskabelon matcher policens forsikringstyper.</Text>
                              ) : (
                                <Select
                                  value={policyTemplatesById[doc.id].selectedTemplateId || undefined}
                                  placeholder="Vælg udbudsskabelon"
                                  loading={policyTemplateSavingById[doc.id]}
                                  disabled={policyTemplateSavingById[doc.id]}
                                  style={{ maxWidth: 420, width: '100%' }}
                                  onChange={(value) => void handlePolicyTemplateChange(doc.id, value)}
                                  options={policyTemplatesById[doc.id].templates.map((template) => ({
                                    value: template.id,
                                    label: `${template.name} (${template.fieldCount} felter)`
                                  }))}
                                />
                              )}
                            </Space>
                          ) : null}
                          {renderPolicyAnalysisActions(doc)}
                          <Collapse
                            ghost
                            items={[
                              {
                                key: 'analysis',
                                label: 'AI analyse',
                                children: renderPolicyAnalysis(doc)
                              }
                            ]}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Space>
        </Card>
      )}

      {leadId ? (
        <TenderWorkspace
          leadId={leadId}
          canManage={canManage}
          analyzedPolicyCount={completedPolicyCount}
        />
      ) : null}

      {lead && (lead.lead_group_id || canManage) ? (
        <Card
          title="Samlekunde"
          loading={Boolean(lead.lead_group_id) && leadGroupLoading}
          extra={
            leadGroup ? (
              <Tag color="blue">{leadGroup.members.length} lead{leadGroup.members.length === 1 ? '' : 's'}</Tag>
            ) : undefined
          }
        >
          {leadGroup ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Space size={8} align="center" wrap>
                <Text strong>{leadGroup.display_name}</Text>
                <Tag color="blue">{leadGroup.members.length} lead{leadGroup.members.length === 1 ? '' : 's'}</Tag>
                <Text type="secondary">
                  Opdateret {formatDateTime(leadGroup.updated_at)}
                </Text>
              </Space>
              {leadGroup.notes ? <Paragraph>{leadGroup.notes}</Paragraph> : null}
              <List
                size="small"
                dataSource={leadGroup.members}
                bordered
                locale={{ emptyText: 'Ingen tilknyttede leads' }}
                renderItem={(member) => {
                  const isCurrent = member.id === lead.id;
                  const statusLabel = member.status
                    ? statusLabelMap.get(member.status) || member.status
                    : 'Ukendt status';
                  const statusColor = member.status
                    ? statusTagColors[member.status] || 'default'
                    : 'default';
                  return (
                    <List.Item
                      key={member.id}
                      actions={
                        canManage
                          ? [
                              <Button
                                key="remove"
                                type="text"
                                danger
                                size="small"
                                loading={removingGroupMemberId === member.id}
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                Fjern
                              </Button>
                            ]
                          : undefined
                      }
                    >
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Space size={8} wrap align="center">
                          <Text strong>{member.company_name || 'Lead'}</Text>
                          {member.cvrnummer ? (
                            <Text type="secondary">
                              CVR {formatCvrNumber(member.cvrnummer)}
                            </Text>
                          ) : null}
                          <Tag color={statusColor}>{statusLabel}</Tag>
                          {isCurrent ? <Tag color="processing">Aktuelt lead</Tag> : null}
                        </Space>
                        <Text type="secondary">Oprettet {formatDateTime(member.created_at)}</Text>
                      </Space>
                    </List.Item>
                  );
                }}
              />
              {canManage ? (
                <Space size={12} wrap>
                  <Button type="primary" onClick={() => openLinkModal('add')}>
                    Tilføj lead til samling
                  </Button>
                  <Button
                    danger
                    onClick={() => lead?.id && handleRemoveMember(lead.id)}
                    loading={Boolean(lead?.id) && removingGroupMemberId === lead?.id}
                  >
                    Fjern dette lead fra samlingen
                  </Button>
                </Space>
              ) : null}
            </Space>
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Text>Dette lead er ikke en del af en samlekunde endnu.</Text>
              {canManage ? (
                <Button type="primary" onClick={() => openLinkModal('create')} disabled={!leadId}>
                  Link til andet lead
                </Button>
              ) : null}
            </Space>
          )}
        </Card>
      ) : null}

      {canManage && (
        <Card
          title="Opgaver"
          extra={
            <Tooltip title="Ny opgave">
              <Button
                icon={<PlusOutlined />}
                type="primary"
                size="small"
                onClick={handleCreateTodoDrawer}
                disabled={!leadId}
              />
            </Tooltip>
          }
          bodyStyle={{ padding: 0 }}
        >
          <List
            loading={todosLoading}
            dataSource={todos}
            locale={{ emptyText: 'Ingen opgaver endnu' }}
            renderItem={(todo) => {
              const isClosed = todo.status === 'DONE' || todo.status === 'CANCELLED';
              const isOverdue =
                !!todo.due_at && !isClosed && new Date(todo.due_at).getTime() < Date.now();

              return (
                <List.Item
                  key={todo.id}
                  actions={[
                    <Tooltip key="history" title="Se historik">
                      <Button
                        type="text"
                        size="small"
                        icon={<HistoryOutlined />}
                        onClick={() => handleOpenTodoHistory(todo)}
                      />
                    </Tooltip>,
                    <Tooltip key="edit" title="Rediger">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditTodoDrawer(todo)}
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={8} wrap>
                        <Text strong>{todo.title}</Text>
                        <Tag color={todoStatusColors[todo.status] || 'default'}>
                          {todoStatusLabels[todo.status] || todo.status}
                        </Tag>
                        <Tag color={todoPriorityColors[todo.priority] || 'default'}>
                          {todoPriorityLabels[todo.priority] || todo.priority}
                        </Tag>
                        {todo.sync_to_calendar ? (
                          <Tooltip title="Synkroniseret til kalender">
                            <CalendarOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        ) : null}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        {todo.description ? (
                          <Space direction="vertical" size={2}>
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{todo.description}</Text>
                            {extractWorkListLink(todo.description) ? (
                              <a href={extractWorkListLink(todo.description) as string}>Åbn arbejdsliste</a>
                            ) : null}
                          </Space>
                        ) : (
                          <Text type="secondary">Ingen beskrivelse</Text>
                        )}
                        <Space size={16} wrap>
                          <Tooltip title={todo.due_at ? formatDateTimeFull(todo.due_at) : undefined}>
                            <Text type={isOverdue ? 'danger' : 'secondary'}>
                              Forfald: {todo.due_at ? formatDateTimeShort(todo.due_at) : 'Ingen'}
                            </Text>
                          </Tooltip>
                          <Text type="secondary">Ansvarlig: {formatAssigneeLabel(todo)}</Text>
                          <Text type="secondary">
                            Opdateret: {formatDateTimeShort(todo.updated_at) || 'Ukendt'}
                          </Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card
            title="Møder"
            extra={
              canManage ? (
                <Tooltip title="Planlæg nyt møde">
                  <Button
                    icon={<PlusOutlined />}
                    type="primary"
                    size="small"
                    onClick={handleCreateMeeting}
                  />
                </Tooltip>
              ) : undefined
            }
            bodyStyle={{ padding: 0 }}
          >
            <List
              dataSource={meetings}
              locale={{ emptyText: canManage ? 'Ingen møder endnu – planlæg det første' : 'Ingen møder endnu' }}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  actions={
                    canManage
                      ? [
                          <Tooltip key="edit" title="Rediger">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEditMeeting(item)}
                            />
                          </Tooltip>,
                          <Tooltip key="delete" title="Slet">
                            <Button
                              danger
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteMeeting(item)}
                              loading={deletingMeetingId === item.id}
                            />
                          </Tooltip>
                        ]
                      : undefined
                  }
                >
                  <List.Item.Meta
                    title={
                      <Space direction="vertical" size={0}>
                        <Text strong>{item.summary || 'Møde'}</Text>
                        <Text type="secondary">{formatDateTime(item.scheduled_for)}</Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text>{item.location || 'Ingen lokation angivet'}</Text>
                        <Text type="secondary">Oprettet {formatDateTime(item.created_at)}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Historik" bodyStyle={{ padding: 0 }}>
            <List
              dataSource={events}
              locale={{ emptyText: 'Ingen historik endnu' }}
              renderItem={(event) => (
                <List.Item key={event.id}>
                  <List.Item.Meta
                    title={
                      <Space direction="vertical" size={0}>
                        <Text strong>{leadEventTypeLabels[event.event_type] || event.event_type}</Text>
                        <Text type="secondary">{formatDateTime(event.created_at)}</Text>
                      </Space>
                    }
                    description={(() => {
                      const items: ReactNode[] = [];
                      const isStatusChange = event.event_type === 'STATUS_CHANGED';
                      const isMeetingEvent = event.event_type.startsWith('MEETING_');

                      const appendParagraphs = (text: string, keyPrefix: string) => {
                        const normalized = text.replace(/\s·\s/g, '\n');
                        const lines = normalized.split(/\n+/).map((line) => line.trim());
                        lines
                          .filter((line) => Boolean(line))
                          .forEach((line, index) => {
                            items.push(
                              <Paragraph key={`${keyPrefix}-${index}`} style={{ marginBottom: 0 }}>
                                {line}
                              </Paragraph>
                            );
                          });
                      };

                      if (isStatusChange && (event.from_status || event.to_status)) {
                        items.push(
                          <Text key="status-change">
                            {statusLabelMap.get(event.from_status || '') || event.from_status || '—'} →{' '}
                            {statusLabelMap.get(event.to_status || '') || event.to_status || '—'}
                          </Text>
                        );
                      }

                      if (isMeetingEvent) {
                        const note = event.notes;
                        if (note) {
                          appendParagraphs(note, 'meeting-note');
                        } else {
                          items.push(
                            <Text key="meeting-empty" type="secondary">
                              Ingen mødedetaljer
                            </Text>
                          );
                        }
                      } else if (event.notes) {
                        appendParagraphs(event.notes, 'event-note');
                      }

                      if (items.length === 0) {
                        return undefined;
                      }

                      return (
                        <Space direction="vertical" size={2}>
                          {items}
                        </Space>
                      );
                    })()}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Card loading={loading} title="Labels">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {sortedLeadLabels.length === 0 ? (
            <Text type="secondary">Ingen labels tilknyttet.</Text>
          ) : (
            <Space size={8} wrap>
              {sortedLeadLabels.map((label) => {
                const tag = (
                  <Tag
                    color={label.color || undefined}
                    style={{ minWidth: 80, textAlign: 'center', fontWeight: 600 }}
                  >
                    {label.name}
                  </Tag>
                );

                return (
                  <Space key={label.id} size={4} align="center">
                    {label.hideInSearch ? (
                      <Tooltip title="Skjuler leadet i virksomhedssøgning">{tag}</Tooltip>
                    ) : (
                      tag
                    )}
                    {canManage && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleDetachLabel(label)}
                        loading={removingLabelId === label.id}
                      >
                        Fjern
                      </Button>
                    )}
                  </Space>
                );
              })}
            </Space>
          )}
          {canManage && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              {allLabels.length === 0 && !labelsLoading ? (
                <Text type="secondary">
                  Der er ingen labels endnu. Opret dem under Tilpasning.
                </Text>
              ) : (
                <Space size="middle" wrap>
                  <Select<string>
                    placeholder="Vælg label"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 220 }}
                    value={selectedLabelId ?? undefined}
                    onChange={(value) => setSelectedLabelId(value ?? null)}
                    options={labelOptions}
                    loading={labelsLoading}
                    disabled={labelsLoading || labelOptions.length === 0}
                    allowClear
                  />
                  <Button
                    type="primary"
                    onClick={handleAttachLabel}
                    loading={attachLoading}
                    disabled={!selectedLabelId || labelsLoading}
                  >
                    Tilknyt label
                  </Button>
                </Space>
              )}
              {!labelsLoading && labelOptions.length === 0 && allLabels.length > 0 && (
                <Text type="secondary">Alle tilgængelige labels er allerede tilknyttet.</Text>
              )}
            </>
          )}
        </Space>
      </Card>

      <Card loading={loading} title="CVR-oplysninger">
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {cvrLookupLoading && <Text type="secondary">Henter CVR-oplysninger...</Text>}
          {cvrLookupError && !cvrLookupLoading && <Text type="danger">{cvrLookupError}</Text>}
          {lead?.cvrnummer ? (
            cvrSummary ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Virksomhedsnavn">
                  {cvrSummary.navn || lead.company_name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {cvrSummary.virksomhed_status || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Branche">
                  {cvrSummary.branchekode || cvrSummary.branchetekst
                    ? [cvrSummary.branchekode, cvrSummary.branchetekst]
                        .filter(Boolean)
                        .join(' - ')
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefon">
                  {cvrSummary.telefon || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="E-mail">
                  {cvrSummary.email || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Adresse">
                  {cvrAddress || '—'}
                </Descriptions.Item>
                {cvrSummary.co_navn && (
                  <Descriptions.Item label="C/O">
                    {cvrSummary.co_navn}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Adresseanvendelse">
                  {cvrSummary.adresseringanvendelse || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Sidst opdateret">
                  {formatDateTime(cvrSummary.opdateret_tidspunkt)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text>
                Ingen CVR-data fundet for {formattedCvr || lead.cvrnummer}.
              </Text>
            )
          ) : (
            <Text type="secondary">
              Tilføj et CVR-nummer for at hente oplysninger fra Datafordeler.
            </Text>
          )}
        </Space>
      </Card>

      <Card title="Detaljer">
        {canManage ? (
          <Form form={notesForm} layout="vertical" onFinish={handleSaveNotes}>
            <Form.Item name="cvrnummer" label="CVR-nummer" rules={[{ validator: validateOptionalCvr }]}>
              <Input placeholder="F.eks. 12345678" maxLength={20} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="contact_name" label="Kontaktperson">
                  <Input placeholder="Navn" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contact_email" label="E-mail" rules={[{ type: 'email', message: 'Ugyldig e-mailadresse' }]}> 
                  <Input placeholder="fx navn@example.dk" allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="contact_phone" label="Telefon">
              <Input placeholder="Telefonnummer" allowClear maxLength={40} />
            </Form.Item>
            <Form.Item name="notes" label="Interne noter">
              <Input.TextArea rows={4} placeholder="Tilføj noter om leadet" allowClear />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={savingNotes}>
              Gem noter
            </Button>
          </Form>
        ) : (
          <Space direction="vertical" size={8}>
            <Paragraph style={{ whiteSpace: 'pre-line' }}>{lead?.notes || 'Ingen noter'}</Paragraph>
            <div>
              <Text strong>CVR-nummer:</Text>{' '}
              {formattedCvr || 'Ikke angivet'}
            </div>
          </Space>
        )}
      </Card>

      <Drawer
        title={editingTodo ? 'Rediger opgave' : 'Ny opgave'}
        open={todoDrawerOpen}
        onClose={handleTodoDrawerClose}
        width={480}
        destroyOnClose
      >
        <Form form={todoForm} layout="vertical" onFinish={handleTodoFormSubmit}>
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Titel skal angives' }]}
          >
            <Input placeholder="Beskriv opgaven" maxLength={160} />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea rows={3} placeholder="Uddybning" allowClear />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Vælg en status' }]}
              >
                <Select
                  options={Object.entries(todoStatusLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Prioritet"
                rules={[{ required: true, message: 'Vælg en prioritet' }]}
              >
                <Select
                  options={Object.entries(todoPriorityLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="due_at" label="Forfaldsdato">
                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="assigned_to" label="Ansvarlig">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Vælg bruger"
                  loading={assigneesLoading}
                  options={assignees.map((user) => ({
                    value: user.id,
                    label: user.full_name?.trim() || user.email || 'Ukendt bruger'
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="sync_to_calendar"
            label="Synkroniser til kalender"
            valuePropName="checked"
            extra="Opretter og opdaterer en kalenderaftale på forfaldstidspunktet"
          >
            <Switch disabled={!watchedTodoDue} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleTodoDrawerClose}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={savingTodo}>
              {editingTodo ? 'Opdater opgave' : 'Opret opgave'}
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={meetingDrawerTitle}
        open={meetingDrawerOpen}
        onClose={handleMeetingDrawerClose}
        width={420}
        destroyOnClose
      >
        <Form form={meetingForm} layout="vertical" onFinish={handleMeetingSubmit}>
          <Form.Item name="scheduled_for" label="Dato og tid">
            <Input type="datetime-local" />
          </Form.Item>
          <Form.Item name="location" label="Lokation">
            <Input placeholder="Lokation" allowClear />
          </Form.Item>
          <Form.Item name="summary" label="Beskrivelse">
            <Input placeholder="Kort beskrivelse" allowClear />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleMeetingDrawerClose}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={savingMeeting}>
              {meetingDrawerSubmitLabel}
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Modal
        title={
          policyInsuranceModalPolicyId
            ? `Tilknyt forsikringstyper: ${
                policyDocuments.find((doc) => doc.id === policyInsuranceModalPolicyId)?.file_name || 'Police'
              }`
            : 'Tilknyt forsikringstyper'
        }
        open={policyInsuranceModalOpen}
        onCancel={handleClosePolicyInsuranceModal}
        onOk={handleSavePolicyInsuranceTypes}
        okText="Gem"
        confirmLoading={policyInsuranceModalSaving}
        destroyOnClose
        width={640}
      >
        <List
          dataSource={availableInsuranceTypes}
          loading={insuranceTypesLoading}
          locale={{ emptyText: 'Ingen forsikringstyper oprettet endnu' }}
          renderItem={(item) => (
            <List.Item key={item.id}>
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <Checkbox
                      checked={selectedPolicyInsuranceTypeIds.includes(item.id)}
                      onChange={(e) => handleTogglePolicyInsuranceType(item.id, e.target.checked)}
                    >
                      {item.title}
                    </Checkbox>
                    {item.category ? <Tag>{item.category}</Tag> : null}
                    {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                  </Space>
                }
                description={
                  item.description ? (
                    <Text>{item.description}</Text>
                  ) : (
                    <Text type="secondary">Ingen beskrivelse</Text>
                  )
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title={isLinkingExistingGroup ? 'Tilføj lead til samling' : 'Saml leads'}
        open={linkModalOpen}
        onCancel={closeLinkModal}
        onOk={handleLinkSubmit}
        okText={isLinkingExistingGroup ? 'Tilføj lead' : 'Opret samling'}
        okButtonProps={{ disabled: !selectedLinkLeadId }}
        confirmLoading={linkModalSubmitting}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Select
            showSearch
            placeholder="Søg efter lead via navn eller CVR"
            value={selectedLinkLeadId ?? undefined}
            onSearch={handleLeadSearch}
            onChange={(value) => setSelectedLinkLeadId(value ?? null)}
            options={leadSearchResults.map((result) => ({
              value: result.id,
              label: `${result.company_name || 'Lead'}${result.cvrnummer ? ` · CVR ${formatCvrNumber(result.cvrnummer)}` : ''}`
            }))}
            loading={leadSearchLoading}
            filterOption={false}
            showArrow={false}
            allowClear
            style={{ width: '100%' }}
            notFoundContent={leadSearchEmptyText}
          />
          <Text type="secondary">
            {isLinkingExistingGroup
              ? 'Søg efter eksisterende leads og tilføj dem til denne samling.'
              : 'Søg efter eksisterende leads og link dem sammen.'}
          </Text>
        </Space>
      </Modal>

      <Modal
        title={historyTodo ? `Historik · ${historyTodo.title}` : 'Opgavehistorik'}
        open={todoHistoryModalOpen}
        onCancel={handleTodoHistoryClose}
        footer={null}
        width={560}
        destroyOnClose
      >
        <List
          loading={todoHistoryLoading}
          dataSource={todoHistory}
          locale={{ emptyText: 'Ingen historik endnu' }}
          renderItem={(event) => (
            <List.Item key={event.id}>
              <List.Item.Meta
                title={
                  <Space direction="vertical" size={2}>
                    <Text strong>{event.description || 'Hændelse'}</Text>
                    <Space size={8} wrap>
                      <Tag color="blue">{event.event_type}</Tag>
                      <Text type="secondary">{formatDateTimeFull(event.created_at)}</Text>
                      {event.actor_full_name ? (
                        <Text type="secondary">{event.actor_full_name}</Text>
                      ) : (
                        <Text type="secondary">System</Text>
                      )}
                    </Space>
                  </Space>
                }
                description={null}
              />
            </List.Item>
          )}
        />
      </Modal>

    </Space>
  );
}
